import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useSearchParam } from '../../../hooks/useSearchParam';
import { PlusIcon, XIcon } from '@phosphor-icons/react';
import * as Dialog from '@radix-ui/react-dialog';
import { toast } from 'react-hot-toast';
import { captureException } from '@sentry/react';

import { PageHeader } from '../../../components/PageHeader';
import { Pagination } from '../../../components/pagination/Pagination';
import { Pill } from '../../../components/pill/Pill';
import { ITableHeader, Table } from '../../../components/table/Table';
import { IncomingDocumentStatus } from 'src/types/enums';
import { getIncomingDocuments, uploadManualIncomingDocument } from '../models';
import { IPaginationVariables, usePagination } from '../../../hooks/usePagination';
import { formatDateTime } from '../../../utils/date';
import {
  IncomingDocumentApprovalModeToText,
  IncomingDocumentRejectionReasonToText,
  IncomingDocumentStatusToColor,
  IncomingDocumentStatusToText,
  IncomingDocumentTypeToText,
} from '../utils/status';
import { SimpleSelect } from '../../../components/select/SimpleSelect';
import { ISimpleComboboxItem } from '../../../components/combobox/SimpleCombobox';
import { INCOMING_DOCUMENT_APPROVAL_MODE_OPTIONS, INCOMING_DOCUMENT_STATUS_OPTIONS } from '../constants';
import { PageHeading } from 'components/PageHeading';
import { Breadcrumb } from 'components/Breadcrumb';
import { FilterField, FilterPanel, ListPage } from 'components/page-templates/ListPage';
import { Button } from '../../../components/button/Button';
import { BaseButton } from '../../../components/button/BaseButton';
import { MultiUploadForm } from '../../document/components/MultiUploadForm';
import { getDisplayError } from '../../../utils/get-display-error';

const BASE_TABLE_HEADERS: ITableHeader[] = [
  {
    id: 'name',
    name: 'Naam bijlage',
  },
  {
    id: 'subject',
    name: 'Onderwerp e-mail',
  },
  {
    id: 'type',
    name: 'Type document',
  },
  {
    id: 'isProcessed',
    name: 'Status',
  },
  {
    id: 'approvalMode',
    name: 'Goedkeuring',
  },
  {
    id: 'date',
    name: 'Datum',
  },
];

const IncomingCmrsPage = () => {
  const [selectedDocStatus, setSelectedDocStatus] = useSearchParam<ISimpleComboboxItem | null>(
    'status',
    INCOMING_DOCUMENT_STATUS_OPTIONS.find((v) => v.key === IncomingDocumentStatus.New) ?? null,
  );
  const [selectedApprovalMode, setSelectedApprovalMode] = useSearchParam<ISimpleComboboxItem | null>(
    'approvalMode',
    null,
  );
  const [isUploadOpen, setIsUploadOpen] = useState(false);
  const isApprovedFilter = selectedDocStatus?.key === IncomingDocumentStatus.Approved;
  const pageFetcher = useCallback(
    async (variables: IPaginationVariables) => {
      return getIncomingDocuments(variables.cursor, variables.take, {
        status: selectedDocStatus ? [selectedDocStatus?.key as IncomingDocumentStatus] : undefined,
        approvedManually: isApprovedFilter && selectedApprovalMode ? selectedApprovalMode.key === 'manual' : undefined,
      });
    },
    [isApprovedFilter, selectedApprovalMode, selectedDocStatus],
  );

  const page = usePagination({
    key: 'id',
    pageSize: 50,
    initialCursor: undefined,
    fetcher: pageFetcher,
  });

  useEffect(() => {
    page.reset();
  }, [selectedDocStatus, selectedApprovalMode]);

  useEffect(() => {
    if (!isApprovedFilter && selectedApprovalMode) {
      setSelectedApprovalMode(null);
    }
  }, [isApprovedFilter, selectedApprovalMode, setSelectedApprovalMode]);

  const tableHeaders = isApprovedFilter
    ? BASE_TABLE_HEADERS
    : BASE_TABLE_HEADERS.filter((header) => header.id !== 'approvalMode');

  const activeFilterCount = [selectedDocStatus, isApprovedFilter && selectedApprovalMode].filter(Boolean).length;

  return (
    <>
      <PageHeader title="Inkomende Documenten" />

      <ListPage
        heading={
          <PageHeading
            noMarginBottom
            leftSide={<Breadcrumb currentItem="Inkomende Documenten" />}
            rightSide={
              <Dialog.Root open={isUploadOpen} onOpenChange={setIsUploadOpen}>
                <Dialog.Trigger asChild>
                  <BaseButton color="primary" iconLeft={<PlusIcon className="button-icon" />}>
                    Upload documenten
                  </BaseButton>
                </Dialog.Trigger>

                <Dialog.Portal>
                  <Dialog.Overlay className="dialog-overlay" />
                  <Dialog.Content className="dialog-content">
                    <Dialog.Title className="heading-one mb-4">Upload inkomende documenten</Dialog.Title>
                    <p className="text-sm text-fg-muted mb-4">
                      PDF&apos;s die je hier uploadt worden door de GPT-analyzer geclassificeerd alsof ze via e-mail
                      binnenkwamen. Bestandsnaam wordt gebruikt om een match met een bestaande order te vinden.
                    </p>

                    <MultiUploadForm
                      onSubmit={async (documents) => {
                        // Serialize: parallel uploads can race on linkOrderDocument
                        // for the same candidate order. Same-type slots (CMR,
                        // leverbon, weegbon) now merge, but keep this sequential
                        // so two uploads don't interleave mid-merge.
                        let success = 0;
                        let failure = 0;
                        for (const doc of documents) {
                          try {
                            await uploadManualIncomingDocument(doc.id);
                            success += 1;
                          } catch (err) {
                            captureException(err);
                            toast.error(`Kon ${doc.name} niet verwerken: ${getDisplayError(err)}`);
                            failure += 1;
                          }
                        }

                        if (success > 0) {
                          toast.success(`${success} document(en) verwerkt`);
                          page.reset();
                        }
                        if (failure === 0) {
                          setIsUploadOpen(false);
                        }
                      }}
                      onCancel={() => setIsUploadOpen(false)}
                    />
                  </Dialog.Content>
                </Dialog.Portal>
              </Dialog.Root>
            }
          />
        }
        filters={
          <FilterPanel
            activeCount={activeFilterCount}
            actions={
              <Button
                size={8}
                color="secondary"
                iconLeft={<XIcon className="button-icon" />}
                onTrigger={() => {
                  setSelectedDocStatus(null);
                  setSelectedApprovalMode(null);
                }}
              >
                Wis alle filters
              </Button>
            }
          >
            {isApprovedFilter && (
              <FilterField label="Goedkeuring">
                <SimpleSelect
                  selectedItem={selectedApprovalMode}
                  items={[...INCOMING_DOCUMENT_APPROVAL_MODE_OPTIONS]}
                  isOptional={true}
                  onSelect={(val) => {
                    if (val?.key === selectedApprovalMode?.key) {
                      setSelectedApprovalMode(null);
                    } else {
                      setSelectedApprovalMode(val);
                    }
                  }}
                />
              </FilterField>
            )}
            <FilterField label="Status">
              <SimpleSelect
                selectedItem={selectedDocStatus}
                items={[...INCOMING_DOCUMENT_STATUS_OPTIONS]}
                isOptional={true}
                onSelect={(val) => {
                  if (val?.key === selectedDocStatus?.key) {
                    setSelectedDocStatus(null);
                  } else {
                    setSelectedDocStatus(val);
                  }
                }}
              />
            </FilterField>
          </FilterPanel>
        }
        pagination={
          <Pagination
            hasPrevious={page.hasPrevious}
            previous={page.previous}
            hasNext={page.hasNext}
            next={page.next}
            isFetching={page.isFetching}
          />
        }
      >
        <Table
          className="h-full"
          idKey="id"
          headers={tableHeaders}
          data={page.data}
          error={page.error}
          onRetry={page.retry}
          isLoading={page.isFetching}
          mapData={(incomingDoc) => {
            const row = [
              <Link to={`${incomingDoc.id}`} className="link-text">
                {incomingDoc.document.name}
              </Link>,
              incomingDoc.subject,
              IncomingDocumentTypeToText[incomingDoc.type],
              <div className="flex flex-col gap-1">
                <Pill color={IncomingDocumentStatusToColor[incomingDoc.status]} size="sm">
                  {IncomingDocumentStatusToText[incomingDoc.status]}
                </Pill>
                {incomingDoc.rejectionReasons.length > 0 && (
                  <div className="flex flex-wrap gap-1">
                    {incomingDoc.rejectionReasons.map((reason) => (
                      <span
                        key={reason}
                        className="text-xs px-1.5 py-0.5 rounded bg-negative-subtle text-negative-text"
                      >
                        {IncomingDocumentRejectionReasonToText[reason]}
                      </span>
                    ))}
                  </div>
                )}
              </div>,
            ];

            if (isApprovedFilter) {
              row.push(
                incomingDoc.status === IncomingDocumentStatus.Approved
                  ? IncomingDocumentApprovalModeToText[incomingDoc.approvedManually ? 'manual' : 'automatic']
                  : '—',
              );
            }

            row.push(formatDateTime(incomingDoc.date));

            return row;
          }}
        />
      </ListPage>
    </>
  );
};

export default IncomingCmrsPage;
